// ═══════════════════════════════════════════════════════════════════════════
// teamContext.js — the team context for the active account, from its
// /api/team/my-accounts row
// ═══════════════════════════════════════════════════════════════════════════
// No React, no network, no storage. App.js builds teamContext from the active
// account's row (loadWorkspaces), and its 60 s poll reloads when the row no
// longer matches the context (membershipChanged).
//
// paid_access: whether the account the user works in pays for the paid-only
// features (AI, the Price Tool), as /api/team/me already says. AdminView reads
// it as `teamContext?.paid_access === true`, which is what unlocks them for an
// invited operator of a paying brand (DOTNET_MIGRATION_PLAN.md section 6,
// Phase 3, "Fix while porting"). Read with `=== true` everywhere, as
// fetchTeamContext reads /api/team/me's, so anything but the JSON boolean true
// is not paid.
//
// The poll compares paid_access only when the row carries the field. While
// Express's my-accounts leaves it out, the poll behaves exactly as before,
// whatever the context says. That includes the context loadWorkspaces builds
// from /api/team/me when my-accounts failed at load: it may say paid, and the
// poll leaves it alone, as the old poll did.
// ═══════════════════════════════════════════════════════════════════════════

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

// The context for one my-accounts row. Same fields and order as before, plus
// paid_access.
export function membershipContext(row) {
  return {
    role:            row.role,
    location_access: row.location_access,
    account_id:      row.account_id,
    account_name:    row.account_name,
    paid_access:     row.paid_access === true,
  };
}

// Whether the active account's row no longer matches the context: its role,
// its location access, or (new) whether it pays for the paid-only features.
// The last is compared only when the row has the field (Express's rows don't).
export function membershipChanged(row, ctx) {
  return row.role !== ctx?.role
    || JSON.stringify(row.location_access || null) !== JSON.stringify(ctx?.location_access || null)
    || (has(row, "paid_access") && (row.paid_access === true) !== (ctx?.paid_access === true));
}
